/**
 * @author colynchen, elvenzhang
 */
import { warn } from './monitor';
import { isPlainObject } from '../util';
import env from '@tencent/vft-common/env';
import { AtomError } from './error';

// eslint-disable-next-line
const API_INVOKE = env.isMac ? 'callHandler' : (env.isWin ? 'CallHandler' : 'invoke') // 调用客户端的接口名称
const API_LISTENER = env.isMac ? 'registerHandler' : 'on'; // 监听客户端回调事件名

function _TenvideoJSBridge() {
  try {
    return window.TenvideoJSBridge || top.TenvideoJSBridge;
  } catch (e) {
    return null;
  }
}

const emitReady = () => {
  try {
    document.dispatchEvent(new Event('onTenvideoJSBridgeReady'));
  } catch (e) {
    if (typeof CustomEvent === 'function') {
      document.dispatchEvent(new CustomEvent('onTenvideoJSBridgeReady'));
    } else if ($) {
      $(document).trigger('onTenvideoJSBridgeReady');
    } else {
      throw e;
    }
  }
};

// 在 JS API 生效前，缓存调用函数
const cacheHandler = [];
document.addEventListener('onTenvideoJSBridgeReady', () => {
  console && console.log(`[atom] TenvideoJSBridge is ready for ${env.device}`);
  for (let i = 0, len = cacheHandler.length; i < len; i++) {
    cacheHandler[i]();
  }
  cacheHandler.length = 0;
}, false);

setTimeout(() => {
  if (env.isApp && env.isWin && window.external) {
    window.TenvideoJSBridge = window.external;
    if (TenvideoJSBridge.AddObserver) {
      TenvideoJSBridge.AddObserver('*');
    }

    const oldObserver = window.QQLive_OnObserve;
    window.QQLive_OnObserve = function (...args) {
      if (typeof oldObserver === 'function') {
        oldObserver.apply(this, args);
      }
      exec(...args);
    };

    emitReady();
  }
}, 10);

/**
 * JSBridge ready 回调
 * @returns {Promise}
 */
export function onTenvideoReady() {
  return new Promise((resolve) => {
    if (!_TenvideoJSBridge()) {
      cacheHandler.push(resolve);
    } else {
      resolve();
    }
  });
}

export function invokeWin(api, params = null, { timeout = 0 } = {}) {
  console.log('invokeWin', api, params);
  return new Promise((resolve, reject) => {
    let timer;
    if (timeout > 0) {
      timer = setTimeout(() => { // 防止接口调用超时
        reject(new AtomError('TenvideoJSBridge 找不到方法 "invoke"', AtomError.BRIDGE_INVOKE_TIMEOUT));
      }, timeout);
    }
    onTenvideoReady().then(() => {
      timer && clearTimeout(timer);
      // eslint-disable-next-line
      const args = Array.prototype.slice.call(arguments)
      args.shift();
      if (TenvideoJSBridge[api]) {
        return resolve(TenvideoJSBridge[api].apply(null, args));
      }
      TenvideoJSBridge.CallHandler(api, JSON.stringify(params));
    });
  });
}

/**
 * 调用客户端 api
 * @param {String} api
 * @param {Object} [params=null] 参数
 * @param {Boolean} [{ timeout = 0 }={}] timeout 调用超时时间
 * @returns {Promise}
 */
export function invoke(api, params = null, { timeout = 0 } = {}) {
  console.log(`[atom] invoke api=${api} params=`, params);
  return new Promise((resolve, reject) => {
    let timer;
    if (timeout > 0) {
      timer = setTimeout(() => { // 防止接口调用超时
        reject(new AtomError('[atom] TenvideoJSBridge inject timeout', AtomError.BRIDGE_INVOKE_TIMEOUT));
      }, timeout);
    }
    onTenvideoReady().then(() => {
      if (!_TenvideoJSBridge()) {
        return reject(new AtomError('[atom] TenvideoJSBridge 未注册，无法使用', AtomError.BRIDGE_INJECT_ERROR));
      }
      if (typeof _TenvideoJSBridge()[API_INVOKE] !== 'function') {
        return reject(new AtomError('[atom] TenvideoJSBridge 找不到方法 "invoke"', AtomError.BRIDGE_INVOKE_FAIL));
      }
      const result = _TenvideoJSBridge()[API_INVOKE](api, params, (data) => {
        console.log(`[atom] invoke callback api=${api} response=`, data);
        timer && clearTimeout(timer);
        if (!isPlainObject(data)) {
          let res = null;
          try {
            res = JSON.parse(data);
          } catch (e) {
            reject(new AtomError(
              `callback result ${JSON.stringify(data)} parse error "${e.message}", maybe is not supported!`,
              AtomError.BRIDGE_INVOKE_ERROR,
            ));
          }
          if (!res) return resolve(data);
          if (!isNaN(+res.errCode) && +res.errCode !== 0) {
            warn(res.errCode, res.errMsg);
            return reject(res);
          } if (isPlainObject(res.result)) {
            return resolve(res.result);
          }
          return resolve(res);
        }
        if (Object.keys(data).length === 0) {
          console && console.warn && console.warn(`[atom] api=${api} 在此版本暂未支持，请升级到最新版本！`);
          return reject(new AtomError(
            `callback return "${JSON.stringify(data)}", maybe is not supported!`,
            AtomError.BRIDGE_INVOKE_ERROR,
          ));
        } if (data.result) {
          return resolve(data.result);
        }
        return resolve(data);
      });

      try {
        if (result) {
          resolve(JSON.parse(result));
          timer && clearTimeout(timer);
        }
      } finally {
      }
    });
  });
}

// 缓存事件监听器
const _events = {};
export function on(event, listener = null, isOnce = false) {
  console.log(`[atom] listen event=${event}`);
  if (typeof listener !== 'function') {
    throw new AtomError('[atom] listener is not a function!', AtomError.BRIDGE_LISTENER_INVALID);
  }

  if (!env.isWin && !_events[event]) {
    onTenvideoReady().then(() => {
      if (_TenvideoJSBridge()) {
        _TenvideoJSBridge()[API_LISTENER](event, exec.bind(null, event));
      }
    });
  }

  if (!_events[event]) {
    _events[event] = [];
  }

  const listeners = _events[event];
  if (_indexOf(listeners, listener) === -1) { // 避免重复注册事件
    listeners.push({
      listener,
      once: isOnce,
    });
  }
}

if (env.isApp && env.isMac) {
  // Mac客户端增加全局消息通知事件处理
  on('onMessage', (params) => {
    if (params && params.event) {
      let { data } = params;
      try {
        data = JSON.parse(data);
      } catch (e) {}
      exec(params.event, data);
    }
  });
}

/**
 * 移除事件存储对象中的监听器
 * @author colynchen
 * @param event
 * @param callback
 */
export function off(event, listener = null) {
  const listeners = _events[event];
  if (!listeners || !listeners.length) {
    return;
  }

  const index = _indexOf(listeners, listener);
  if (index !== -1) { // 解除事件
    listeners.splice(index, 1);
  }
}

/**
 * 执行事件回调
 * @param {String} event      事件名称
 * @param {Object} jsonText   回调数据
 */
function exec(event, jsonText) {
  let res = {};

  if (isPlainObject(jsonText)) {
    res = jsonText;
  } else {
    try {
      res = JSON.parse(jsonText);
    } catch (e) {
      if (!env.isApp && !env.isWin) {
        console.log(e);
      }
    }
  }

  const $events = _events[event];
  if ($events && $events.length > 0) {
    const len = $events.length;
    for (let i = 0; i < len; i++) {
      if ($events[i] && typeof $events[i].listener === 'function') {
        $events[i].listener(res);
        if ($events[i].once) {
          $events.splice(i, 1, null);
        }
      }
    }
    const tempEvents = [];
    for (let i = 0; i < len; i++) {
      if ($events[i]) {
        tempEvents.push($events[i]);
      }
    }
    _events[event] = tempEvents;
    console && console.log(`[atom] listen callback event=${event}`, jsonText);
  }
}

/**
 * 判断两个监听器是否一致
 * @param {Array} array 事件数组
 * @param {function} listener 监听器
 */
function _indexOf(array, listener) {
  let result = -1;

  for (let i = 0, len = array.length; i < len; i++) {
    // 由于直接对比 function 在同一场景下多次调用时，会导致两个 function 不相等的情况，故通过转字符串进行对比
    if (array[i].listener === listener) {
      result = i;
      break;
    }
  }

  return result;
}
